import React from 'react';
import { X, ChevronRight, ChevronLeft, Users, FileText, User, Search, Check } from 'lucide-react';
import { getCommunicationChatContext } from '../services/communicationChatService';

const INTENTS = [
    { id: 'RFP_GENERAL', title: 'Everyone on this proposal', description: 'Shared thread for the whole RFP' },
    { id: 'RFP_SECTION', title: 'A section', description: 'Shared thread for one chapter' },
    { id: 'RFP_DM', title: 'Message one person', description: 'Private 1:1 within this RFP' },
    // { id: 'RFP_SECTION_DM', title: 'Section group', description: 'Private group tied to one section' },
];

const getDisplayName = (person) =>
    person?.display_name || person?.full_name || person?.name || person?.email || 'Unknown';

const getUserId = (person) => person?.user_id || person?.id || person?._id;

const formatRole = (role) => {
    if (!role) return '';
    return role
        .split(/[_-]/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

const NewThreadWizard = ({ isOpen, rfpId, currentUserId, onClose, onResolveThread }) => {
    const [step, setStep] = React.useState(1);
    const [intent, setIntent] = React.useState('');
    const [contextData, setContextData] = React.useState(null);
    const [selectedSection, setSelectedSection] = React.useState(null);
    const [selectedDmUser, setSelectedDmUser] = React.useState(null);
    const [groupMembers, setGroupMembers] = React.useState([]);
    const [search, setSearch] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');

    const needsContext = Boolean(intent);

    React.useEffect(() => {
        if (!isOpen || !rfpId || contextData) return;
        const loadContext = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await getCommunicationChatContext(rfpId);
                setContextData(response?.data || {});
            } catch (err) {
                setError(err?.response?.data?.detail || err?.response?.data?.message || 'Failed to load chat context.');
            } finally {
                setLoading(false);
            }
        };
        loadContext();
    }, [contextData, isOpen, rfpId]);

    React.useEffect(() => {
        if (!isOpen) {
            setStep(1);
            setIntent('');
            setContextData(null);
            setSelectedSection(null);
            setSelectedDmUser(null);
            setGroupMembers([]);
            setSearch('');
            setError('');
            setSubmitting(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const sections = contextData?.sections || [];
    const people = contextData?.all_people || [];
    const currentUserString = currentUserId ? String(currentUserId) : '';
    const filteredPeople = people.filter((person) => String(getUserId(person)) !== currentUserString);

    const visibleSections = sections.filter((section) => {
        const q = search.toLowerCase();
        if (!q) return true;
        return `${section?.title || ''} ${section?.section_key || ''}`.toLowerCase().includes(q);
    });

    const visiblePeople = filteredPeople.filter((person) => getDisplayName(person).toLowerCase().includes(search.toLowerCase()));
    const visibleAllPeople = filteredPeople.filter((person) => getDisplayName(person).toLowerCase().includes(search.toLowerCase()) || (person?.role || '').toLowerCase().includes(search.toLowerCase()));
    const sectionAssignees = (selectedSection?.assignees || []).filter((person) => String(getUserId(person)) !== currentUserString);
    const visibleAssignees = sectionAssignees.filter((person) => getDisplayName(person).toLowerCase().includes(search.toLowerCase()));

    const canGoNextFromStep1 = Boolean(intent);
    const canGoNextFromStep2 =
        intent === 'RFP_SECTION'
            ? Boolean(selectedSection)
            : intent === 'RFP_DM'
                ? Boolean(selectedDmUser)
                : intent === 'RFP_SECTION_DM'
                    ? Boolean(selectedSection) && groupMembers.length >= 1
                    : true;

    const confirmText = (() => {
        if (intent === 'RFP_GENERAL') return 'Start conversation: Everyone on this proposal';
        if (intent === 'RFP_SECTION') return `Start section chat: ${selectedSection?.title || 'Selected section'}`;
        if (intent === 'RFP_DM') return `Start direct message with: ${getDisplayName(selectedDmUser)}`;
        if (intent === 'RFP_SECTION_DM') {
            return `Start group chat on section: ${selectedSection?.title || 'Selected section'} with ${groupMembers.length + 1} people`;
        }
        return 'Choose a thread type';
    })();

    const buildResolvePayload = () => {
        if (intent === 'RFP_GENERAL') return { scope_type: 'RFP_GENERAL' };
        if (intent === 'RFP_SECTION') {
            return {
                scope_type: 'RFP_SECTION',
                section_id: selectedSection?.section_id,
            };
        }
        if (intent === 'RFP_DM') {
            return {
                scope_type: 'RFP_DM',
                participant_user_ids: [currentUserId, getUserId(selectedDmUser)],
            };
        }
        if (intent === 'RFP_SECTION_DM') {
            const memberIds = groupMembers.map((person) => getUserId(person));
            return {
                scope_type: 'RFP_SECTION_DM',
                section_id: selectedSection?.section_id,
                participant_user_ids: [currentUserId, ...memberIds],
            };
        }
        return null;
    };

    const handleNext = () => {
        if (step === 1 && intent) {
            setStep(2);
        } else if (step === 2 && canGoNextFromStep2) {
            setStep(3);
        }
    };

    const handleIntentClick = (newIntentId) => {
        setIntent(newIntentId);
        // Auto-advance after a brief delay to show the selected state animation
        setTimeout(() => {
            setStep(2);
        }, 150);
    };

    const handleBack = () => {
        if (step === 3 && needsContext) {
            setStep(2);
            return;
        }
        if (step > 1) {
            setStep(step - 1);
        }
    };

    const toggleGroupMember = (person) => {
        const id = String(getUserId(person));
        setGroupMembers((prev) => {
            const exists = prev.some((member) => String(getUserId(member)) === id);
            if (exists) {
                return prev.filter((member) => String(getUserId(member)) !== id);
            }
            return [...prev, person];
        });
    };

    const handleResolve = async () => {
        const payload = buildResolvePayload();
        if (!payload) return;
        setSubmitting(true);
        setError('');
        try {
            const threadId = await onResolveThread(payload);
            if (threadId) onClose();
            if (!threadId) setError('Unable to open thread. Please try again.');
        } catch (err) {
            setError(err?.message || 'Failed to start conversation.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="v-ch-wizard-overlay">
            <div className="v-ch-wizard">
                <header className="v-ch-wizard-header">
                    <h4>Start Conversation</h4>
                    <button className="v-ch-icon-btn" onClick={onClose} title="Close">
                        <X size={16} />
                    </button>
                </header>

                <div className="v-ch-wizard-steps">
                    <span
                        className={`${step === 1 ? 'active' : ''} ${submitting ? 'step-disabled' : 'step-clickable'}`}
                        onClick={() => { if (!submitting) setStep(1); }}
                    >
                        Conversation Type
                    </span>
                    <ChevronRight size={14} className="vch-step-divider" />
                    <span
                        className={`${step === 2 ? 'active' : ''} ${intent && needsContext && !submitting ? 'step-clickable' : 'step-disabled'}`}
                        onClick={() => { if (intent && needsContext && !submitting) setStep(2); }}
                    >
                        Details
                    </span>
                    <ChevronRight size={14} className="vch-step-divider" />
                    <span
                        className={`${step === 3 ? 'active' : ''} ${canGoNextFromStep2 && !submitting ? 'step-clickable' : 'step-disabled'}`}
                        onClick={() => { if (canGoNextFromStep2 && !submitting) setStep(3); }}
                    >
                        Review and Start
                    </span>
                </div>

                <div className="v-ch-wizard-body">
                    {error ? <div className="v-ch-error-banner">{error}</div> : null}

                    {step === 1 && (
                        <div className="vch-step-content-wrapper">
                            <div className="vch-step-header">
                                <h3 className="vch-step-title">Choose a conversation type</h3>
                                <p className="vch-step-subtitle">Select how you want to start this conversation.</p>
                            </div>
                            <div className="vch-intent-cards-container">
                                {INTENTS.map((option) => {
                                    const isActive = intent === option.id;
                                    let IconComp = Users;
                                    let themeClass = 'blue';
                                    let badgeText = '👥 12 Participants';

                                    if (option.id === 'RFP_SECTION') {
                                        IconComp = FileText;
                                        themeClass = 'green';
                                        badgeText = `📄 ${sections.length > 0 ? `${sections.length} Sections Available` : '24 Sections Available'}`;
                                    } else if (option.id === 'RFP_DM') {
                                        IconComp = User;
                                        themeClass = 'purple';
                                        badgeText = '🔒 Private Discussion';
                                    } else if (option.id === 'RFP_GENERAL') {
                                        const count = people.length > 0 ? people.length + 1 : 12;
                                        badgeText = `👥 ${count} Participants`;
                                    }

                                    return (
                                        <button
                                            key={option.id}
                                            className={`vch-intent-premium-card ${isActive ? 'active' : ''}`}
                                            onClick={() => handleIntentClick(option.id)}
                                        >
                                            <div className={`vch-intent-icon-wrap ${themeClass}`}>
                                                <IconComp size={22} />
                                            </div>
                                            <div className="vch-intent-details">
                                                <span className="vch-intent-card-title">{option.title}</span>
                                                <span className="vch-intent-card-desc">{option.description}</span>
                                                <span className="vch-intent-card-badge">{badgeText}</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="v-ch-picker-wrap">
                            {loading ? <div className="v-ch-empty-state">Loading chat context...</div> : null}
                            {!loading && (
                                <>
                                    <div className="vch-step-header">
                                        <h3 className="vch-step-title">Add details</h3>
                                        <p className="vch-step-subtitle">Add people or select sections for this conversation.</p>
                                    </div>

                                    <div className="vch-search-wrapper">
                                        <Search size={16} className="vch-search-icon" />
                                        <input
                                            className="v-ch-picker-search vch-search-input-with-icon"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder={
                                                intent === 'RFP_SECTION'
                                                    ? "Search sections..."
                                                    : intent === 'RFP_DM'
                                                        ? "Search people by name, role, or department..."
                                                        : intent === 'RFP_GENERAL'
                                                            ? "Search participants by name or role..."
                                                            : "Search..."
                                            }
                                        />
                                    </div>

                                    {intent === 'RFP_SECTION' && (
                                        <div className="vch-section-picker-list">
                                            {visibleSections.map((section) => {
                                                const isSelected = selectedSection?.section_id === section.section_id;
                                                const assignees = section.assignees || (section.assigned_to ? (Array.isArray(section.assigned_to) ? section.assigned_to : [section.assigned_to]) : []);
                                                const assignee = assignees[0];

                                                return (
                                                    <button
                                                        key={section.section_id}
                                                        className={`vch-section-picker-row ${isSelected ? 'active' : ''}`}
                                                        onClick={() => {
                                                            setSelectedSection(section);
                                                            setTimeout(() => setStep(3), 150);
                                                        }}
                                                    >
                                                        <div className="vch-section-row-left">
                                                            <span className="vch-section-row-name">
                                                                {section.title ? section.title.toUpperCase() : 'UNTITLED SECTION'}
                                                            </span>
                                                            <div className="vch-section-row-assignee-info">
                                                                {assignee ? (
                                                                    <>
                                                                        <div className="vch-section-avatar">
                                                                            {assignee.avatar ? (
                                                                                <img src={assignee.avatar} alt={getDisplayName(assignee)} />
                                                                            ) : (
                                                                                <span>{getDisplayName(assignee).substring(0, 2).toUpperCase()}</span>
                                                                            )}
                                                                        </div>
                                                                        <span className="vch-section-assignee-name">
                                                                            Assigned to {getDisplayName(assignee)}
                                                                        </span>
                                                                    </>
                                                                ) : (
                                                                    <span className="vch-section-unassigned-badge">
                                                                        Unassigned
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {intent === 'RFP_DM' && (
                                        <div className="vch-person-picker-list">
                                            {visiblePeople.map((person) => {
                                                const isSelected = getUserId(selectedDmUser) === getUserId(person);
                                                const presence = person?.presence || (getUserId(person) % 3 === 0 ? 'away' : getUserId(person) % 5 === 0 ? 'offline' : 'online');
                                                const roleText = formatRole(person?.role || person?.designation || 'Proposal Contributor');
                                                const deptText = person?.department;

                                                return (
                                                    <button
                                                        key={getUserId(person)}
                                                        className={`vch-person-picker-row ${isSelected ? 'active' : ''}`}
                                                        onClick={() => {
                                                            setSelectedDmUser(person);
                                                            setTimeout(() => setStep(3), 200);
                                                        }}
                                                    >
                                                        <div className="vch-person-row-left">
                                                            <div className="vch-person-avatar-wrapper">
                                                                <div className="vch-person-avatar">
                                                                    {person.avatar || person.profile_picture ? (
                                                                        <img src={person.avatar || person.profile_picture} alt={getDisplayName(person)} />
                                                                    ) : (
                                                                        <span>{getDisplayName(person).substring(0, 2).toUpperCase()}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="vch-person-details">
                                                                <span className="vch-person-name">{getDisplayName(person)}</span>
                                                                <span className="vch-person-role-dept">
                                                                    {roleText}{deptText ? ` \u2022 ${deptText}` : ''}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {intent === 'RFP_SECTION_DM' && (
                                        <div className="v-ch-section-group-step">
                                            <div className="v-ch-picker-list">
                                                {visibleSections.map((section) => (
                                                    <button
                                                        key={section.section_id}
                                                        className={`v-ch-picker-row ${selectedSection?.section_id === section.section_id ? 'active' : ''}`}
                                                        onClick={() => {
                                                            setSelectedSection(section);
                                                            setGroupMembers([]);
                                                        }}
                                                    >
                                                        <span>{section.title}</span>
                                                        <small>{section.section_key || 'Section'}</small>
                                                    </button>
                                                ))}
                                            </div>
                                            {selectedSection ? (
                                                <div className="v-ch-picker-list">
                                                    {visibleAssignees.map((person) => {
                                                        const checked = groupMembers.some((member) => String(getUserId(member)) === String(getUserId(person)));
                                                        return (
                                                            <button
                                                                key={getUserId(person)}
                                                                className={`v-ch-picker-row ${checked ? 'active' : ''}`}
                                                                onClick={() => toggleGroupMember(person)}
                                                            >
                                                                <span>{getDisplayName(person)}</span>
                                                                <small>{checked ? 'Selected' : (person?.role || person?.email || 'Assignee')}</small>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            ) : null}
                                        </div>
                                    )}
                                    {intent === 'RFP_GENERAL' && (
                                        <div className="vch-person-picker-list">
                                            {visibleAllPeople.map((person) => {
                                                const presence = person?.presence || (getUserId(person) % 3 === 0 ? 'away' : getUserId(person) % 5 === 0 ? 'offline' : 'online');
                                                const roleText = formatRole(person?.role || person?.designation || 'Proposal Contributor');
                                                const deptText = person?.department;

                                                return (
                                                    <div
                                                        key={getUserId(person)}
                                                        className="vch-person-picker-row disabled-hover"
                                                    >
                                                        <div className="vch-person-row-left">
                                                            <div className="vch-person-avatar-wrapper">
                                                                <div className="vch-person-avatar">
                                                                    {person.avatar || person.profile_picture ? (
                                                                        <img src={person.avatar || person.profile_picture} alt={getDisplayName(person)} />
                                                                    ) : (
                                                                        <span>{getDisplayName(person).substring(0, 2).toUpperCase()}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="vch-person-details">
                                                                <span className="vch-person-name">{getDisplayName(person)}</span>
                                                                <span className="vch-person-role-dept">
                                                                    {roleText}{deptText ? ` \u2022 ${deptText}` : ''}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {step === 3 && (
                        <div className="vch-step-content-wrapper">
                            <div className="vch-step-header">
                                <h3 className="vch-step-title">Review your conversation</h3>
                                <p className="vch-step-subtitle">Please review the details before starting the conversation.</p>
                            </div>
                            <div className="v-ch-confirm-block">
                                <p>{confirmText}</p>
                            </div>
                        </div>
                    )}
                </div>

                <footer className="v-ch-wizard-footer">
                    <button className="v-ch-ghost-btn" onClick={handleBack} disabled={step === 1 || submitting}>
                        ← Back
                    </button>
                    {step === 2 && intent === 'RFP_GENERAL' ? (
                        <button className="v-ch-primary-btn" onClick={() => setStep(3)}>
                            Continue →
                        </button>
                    ) : null}
                    {step === 3 ? (
                        <button className="v-ch-primary-btn" onClick={handleResolve} disabled={submitting}>
                            {submitting ? 'Starting...' : 'Start conversation'}
                        </button>
                    ) : null}
                </footer>
            </div>
        </div>
    );
};

export default NewThreadWizard;
